import DOMPurify from "dompurify";
import { absoluteUrl } from "@/api/client";
import { printHtml, currency } from "@/lib/printDoc";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
const clean = (h) => DOMPurify.sanitize(h || "", { ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "h4", "ul", "ol", "li", "div", "span"], ALLOWED_ATTR: [] });

export function feeTotals(fees) {
  const subtotal = (fees?.lines || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const gst = subtotal * (parseFloat(fees?.gst_percent) || 0) / 100;
  const milestoneTotal = (fees?.milestones || []).reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  return { subtotal, gst, grand: subtotal + gst, milestoneTotal };
}

export const pctOf = (amount, base) => (base ? ((parseFloat(amount) || 0) / base) * 100 : 0);

function feesHtml(fees, n) {
  const t = feeTotals(fees);
  const gstPct = parseFloat(fees.gst_percent) || 0;
  const base = parseFloat(fees.milestone_base) || 0;
  const lines = (fees.lines || []).map((l) => `<tr><td>${esc(l.label)}</td><td class="num">${currency(l.amount)} + GST</td></tr>`).join("");
  const ms = (fees.milestones || []).map((m, i) => `<tr><td>${String(i + 1).padStart(2, "0")}</td><td>${esc(m.trigger)}</td><td class="num">~${pctOf(m.amount, base).toFixed(1)}%</td><td class="num">${currency(m.amount)}</td></tr>`).join("");
  return `
    <h4>${n}.1 Fee Structure</h4>
    <table class="items"><tbody>${lines}
      <tr><td>GST @ ${gstPct}%${gstPct === 18 ? " (9% CGST + 9% SGST)" : ""}</td><td class="num">${currency(Math.round(t.gst))}</td></tr>
      <tr><td><b>Total Work Order Value</b></td><td class="num"><b>${currency(t.subtotal)} + GST (${currency(Math.round(t.grand))} incl. GST)</b></td></tr>
    </tbody></table>
    ${fees.note ? `<p style="font-size:11px;color:#525252;"><i>Note: ${esc(fees.note)}</i></p>` : ""}
    ${ms ? `<h4>${n}.2 ${esc(fees.milestone_title || "Milestone Payment Schedule")}</h4>
    <table class="items"><thead><tr><th style="width:36px;">#</th><th>Stage / Trigger Description</th><th class="num" style="width:80px;">Percentage</th><th class="num" style="width:120px;">Amount (Excl. Tax)</th></tr></thead>
      <tbody>${ms}<tr><td></td><td><b>Total Fee</b></td><td class="num"><b>${pctOf(t.milestoneTotal, base).toFixed(0)}%</b></td><td class="num"><b>${currency(t.milestoneTotal)} + GST</b></td></tr></tbody></table>` : ""}`;
}

const fmtDate = (d) => {
  const dt = d ? new Date(`${d}T00:00:00`) : null;
  return dt && !isNaN(dt) ? dt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "____________";
};

export function buildAgreementHtml(a, workspace) {
  const lh = workspace?.letterhead_url;
  const header = lh
    ? `<div style="text-align:center;margin-bottom:12px;"><img class="letterhead" src="${esc(absoluteUrl(lh))}" alt="" /></div>`
    : `<div style="display:flex;align-items:center;gap:10px;border-bottom:2px solid #171717;padding-bottom:10px;margin-bottom:16px;">
        ${workspace?.logo_url ? `<img src="${esc(absoluteUrl(workspace.logo_url))}" style="width:44px;height:44px;object-fit:contain;" />` : ""}
        <div><div class="text-xl font-bold">${esc(workspace?.name || "Gray Owl Studio")}</div>
        ${workspace?.tagline ? `<div style="font-size:10px;color:#737373;text-transform:uppercase;letter-spacing:.05em;">${esc(workspace.tagline)}</div>` : ""}</div></div>`;
  const sections = (a.sections || []).map((s, i) => `
    <div class="wo-sec"><h3>${i + 1}. ${esc(s.heading)}</h3>
      ${s.kind === "fees" ? feesHtml(a.fees || {}, i + 1) : ""}
      ${s.kind === "fees" && s.body_html ? `<div>${clean(s.body_html).replace(/<h4>/, `<h4>${i + 1}.3 `)}</div>` : clean(s.body_html)}
    </div>`).join("");
  const sigs = (a.signatories || []).map((s) => `
    <div class="sig"><div class="font-bold">${esc(s.party)}</div><div style="font-size:11px;color:#525252;">${esc(s.role)}</div>
      <div style="margin-top:28px;">Signature: ______________________</div>
      <div style="margin-top:8px;">Name: ${esc(s.name)}</div><div style="margin-top:8px;">Date: ______________________</div></div>`).join("");
  return `
    <style>
      .wo h3 { font-size:14px; margin:18px 0 6px; padding-bottom:4px; border-bottom:1px solid #e5e5e5; }
      .wo h4 { font-size:12px; margin:10px 0 4px; }
      .wo p { font-size:11.5px; line-height:1.55; margin:0 0 6px; }
      .wo li { font-size:11.5px; line-height:1.5; margin:0 0 2px; }
      .wo ul, .wo ol { margin:0 0 8px; padding-left:20px; }
      .wo .items { margin:4px 0 10px; } .wo .items td { font-size:11px; }
      .wo .sigs { display:grid; grid-template-columns:1fr 1fr; gap:28px 40px; margin-top:16px; font-size:12px; }
      .wo .sig { break-inside: avoid; }
    </style>
    <div class="wo">
      ${header}
      <div style="text-align:center;margin-bottom:14px;">
        <div style="font-size:18px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;">${esc(a.title)}</div>
        ${a.project_name ? `<div style="font-size:12px;margin-top:4px;"><b>Project:</b> ${esc(a.project_name)}</div>` : ""}
        <div style="font-size:11px;color:#525252;margin-top:2px;">${a.wo_number ? `Ref: ${esc(a.wo_number)} · ` : ""}Date: ${esc(fmtDate(a.date))}</div>
      </div>
      <div class="section" style="font-size:12px;margin-bottom:10px;">
        <div style="font-size:10px;color:#737373;text-transform:uppercase;letter-spacing:.05em;">Between</div>
        <div><b>Design Consultant:</b> ${esc(a.consultant_name)}</div>
        <div><b>Client:</b> ${esc(a.client_name)}</div>
      </div>
      ${a.intro_heading || a.intro_html ? `<div class="wo-sec"><h3>${esc(a.intro_heading)}</h3>${clean(a.intro_html)}</div>` : ""}
      ${sections}
      <div class="wo-sec" style="break-inside:avoid;"><h3>Signatures &amp; Acceptance</h3><p>${esc(a.acceptance_text)}</p><div class="sigs">${sigs}</div></div>
    </div>`;
}

export function printAgreement(a, workspace) {
  printHtml(buildAgreementHtml(a, workspace), { title: `${a.wo_number || "Work Order"} — ${a.title}`, pageSize: "A4" });
}
